import React, { useState, useEffect } from "react";
import axios from "axios";
import { Printer, X, Copy, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const PAPER = { 58: { width: "48mm", font: "11px", cols: 32 }, 80: { width: "72mm", font: "12px", cols: 42 } };

const buildLines = (inv) => {
  const diskon = Number(inv.diskon || 0);
  const total = Math.max(Number(inv.total_tagihan || 0) - diskon, 0);
  const lines = [
    ["No. Struk", inv.no_invoice],
    ["Tanggal", (inv.tanggal_bayar || inv.tanggal_terbit || "").slice(0, 16)],
    ["Kasir", inv.kasir_nama || "Sistem"],
    ["Pelanggan", inv.pelanggan_nama],
  ];
  if (inv.no_layanan) lines.push(["No. Layanan", inv.no_layanan]);
  if (inv.periode_bulan || inv.periode_kode) lines.push(["Periode", inv.periode_bulan || inv.periode_kode]);
  return { lines, diskon, total, paid: inv.status === "PAID" };
};

const receiptHtml = (inv, c, size) => {
  const { lines, diskon, total, paid } = buildLines(inv);
  const p = PAPER[size];
  const phone = c.whatsapp_cs || c.telepon;
  const row = (l, r, cls = "") => `<div class="r ${cls}"><span>${esc(l)}</span><span>${esc(r)}</span></div>`;
  return `<!doctype html><html><head><title>Struk ${esc(inv.no_invoice)}</title><style>
@page { size: ${size}mm auto; margin: 2mm; }
* { box-sizing: border-box; }
body { margin: 0; font-family: 'Courier New', monospace; font-size: ${p.font}; color: #000; }
.w { width: ${p.width}; margin: 0 auto; }
.c { text-align: center; } .b { font-weight: bold; }
.logo { max-width: 60%; max-height: 18mm; filter: grayscale(1) contrast(1.4); display: block; margin: 0 auto 2px; }
.h { font-size: 1.25em; font-weight: bold; }
.s { font-size: 0.85em; line-height: 1.3; }
.d { border-top: 1px dashed #000; margin: 4px 0; }
.r { display: flex; justify-content: space-between; gap: 6px; } .r span:last-child { text-align: right; word-break: break-word; }
.t { font-size: 1.1em; font-weight: bold; }
</style></head><body><div class="w">
${c.logo ? `<img class="logo" src="${esc(c.logo)}" alt="logo"/>` : ""}
<div class="c h">${esc(c.nama_isp || "")}</div>
<div class="c s">${c.badan_usaha ? `${esc(c.badan_usaha)}<br/>` : ""}${c.alamat ? `${esc(c.alamat)}<br/>` : ""}${phone ? `Telp/WA: ${esc(phone)}` : ""}</div>
<div class="d"></div>
${lines.map(([l, r]) => row(l, r)).join("")}
<div class="d"></div>
<div class="b">Internet ${esc(inv.paket_nama || "")}</div>
${row("", rp(inv.nominal ?? inv.total_tagihan))}
${diskon > 0 ? row("Diskon", `-${rp(diskon)}`) : ""}
${Number(inv.pajak_ppn || 0) > 0 ? row("PPN", rp(inv.pajak_ppn)) : ""}
<div class="d"></div>
${row("TOTAL", rp(total), "t")}
${inv.status === "PARTIAL" ? row("Dibayar", rp(inv.jumlah_dibayar)) + row("Sisa", rp(total - Number(inv.jumlah_dibayar || 0))) : ""}
${row("Status", paid ? "LUNAS" : inv.status === "PARTIAL" ? "CICILAN" : "BELUM LUNAS", "b")}
${row("Metode", inv.metode_pembayaran || "-")}
<div class="d"></div>
<div class="c s">${esc(c.invoice_footer || "Terima kasih telah berlangganan!")}</div>
<div class="c s">Simpan struk ini sebagai bukti pembayaran.</div>
</div><script>window.onload=function(){setTimeout(function(){window.print();},300);};</script></body></html>`;
};

const receiptText = (inv, c, size) => {
  const { lines, diskon, total, paid } = buildLines(inv);
  const n = PAPER[size].cols;
  const center = (s) => { s = String(s || ""); const pad = Math.max(0, Math.floor((n - s.length) / 2)); return " ".repeat(pad) + s; };
  const lr = (l, r) => { l = String(l); r = String(r ?? ""); return l + " ".repeat(Math.max(1, n - l.length - r.length)) + r; };
  const eq = "=".repeat(n), dash = "-".repeat(n);
  return [eq, center(c.nama_isp), c.badan_usaha && center(c.badan_usaha), c.alamat && center(c.alamat),
    (c.whatsapp_cs || c.telepon) && center(`WA: ${c.whatsapp_cs || c.telepon}`), eq,
    ...lines.map(([l, r]) => lr(`${l}:`, r)), dash, `Internet ${inv.paket_nama || ""}`, lr("", rp(inv.nominal ?? inv.total_tagihan)),
    diskon > 0 && lr("Diskon:", `-${rp(diskon)}`), dash, lr("TOTAL:", rp(total)),
    lr("Status:", paid ? "LUNAS" : inv.status === "PARTIAL" ? "CICILAN" : "BELUM LUNAS"), lr("Metode:", inv.metode_pembayaran || "-"),
    eq, center(c.invoice_footer || "Terima kasih"), eq].filter(Boolean).join("\n");
};

export const ThermalReceiptModal = ({ invoice, onClose }) => {
  const [comp, setComp] = useState(null);
  const [size, setSize] = useState(() => Number(localStorage.getItem("thermal_paper")) || 58);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/pengaturan/perusahaan`, { withCredentials: true })
      .then((r) => setComp(r.data || {}))
      .catch(() => setComp({}));
  }, []);

  if (!invoice) return null;
  const chooseSize = (s) => { setSize(s); localStorage.setItem("thermal_paper", String(s)); };

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=420,height=640");
    if (!w) { alert("Pop-up diblokir browser. Izinkan pop-up untuk mencetak struk."); return; }
    w.document.write(receiptHtml(invoice, comp, size));
    w.document.close();
  };

  const copyText = () => {
    navigator.clipboard.writeText(receiptText(invoice, comp, size));
    alert("Format struk berhasil disalin ke clipboard!");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="thermal-modal">
      <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-[#090f1d]">
          <div className="flex items-center gap-2">
            <Printer className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-bold text-white">Struk Thermal</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-700 overflow-hidden" data-testid="thermal-size-toggle">
              {[58, 80].map((s) => (
                <button key={s} onClick={() => chooseSize(s)} data-testid={`thermal-size-${s}`}
                  className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${size === s ? "bg-cyan-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
                  {s}mm
                </button>
              ))}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-md" data-testid="thermal-close-btn"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex justify-center bg-slate-950/60">
          {!comp ? (
            <p className="text-xs text-slate-400 py-10 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Memuat profil usaha...</p>
          ) : (
            <iframe title="Pratinjau struk" srcDoc={receiptHtml(invoice, comp, size).replace(/<script>.*<\/script>/, "")}
              data-testid="thermal-preview"
              className="bg-white rounded-lg shadow-lg border border-slate-300 h-[460px]"
              style={{ width: size === 58 ? 230 : 320 }} />
          )}
        </div>

        <div className="p-3 bg-[#0d1527] border-t border-slate-800 flex gap-2">
          <button onClick={copyText} disabled={!comp} data-testid="thermal-copy-btn"
            className="flex-1 py-2 px-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors">
            <Copy className="w-3.5 h-3.5" /><span>Salin Teks</span>
          </button>
          <button onClick={handlePrint} disabled={!comp} data-testid="print-thermal-btn"
            className="flex-1 py-2 px-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-lg shadow-cyan-600/30">
            <Printer className="w-3.5 h-3.5" /><span>Cetak {size}mm</span>
          </button>
        </div>
      </div>
    </div>
  );
};
